
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormField, FileUpload } from "./BaseForm";
import { EmployeeMedicalFormData, FormProps } from "@/types/forms";

type EmployeeMedicalFormProps = FormProps<EmployeeMedicalFormData>;

const EmployeeMedicalForm = ({ onSubmit, initialData = {}, buttonText = "Continue" }: EmployeeMedicalFormProps) => {
  const [formData, setFormData] = useState<EmployeeMedicalFormData>({
    numberOfEmployees: initialData.numberOfEmployees || "",
    budgetPerEmployee: initialData.budgetPerEmployee || "",
    comments: initialData.comments || "",
    censuFile: initialData.censuFile || null,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.numberOfEmployees) newErrors.numberOfEmployees = "Number of employees is required";
    if (!formData.budgetPerEmployee) newErrors.budgetPerEmployee = "Budget per employee is required";
    if (!formData.comments) newErrors.comments = "Comments are required";
    if (!formData.censuFile) newErrors.censuFile = "Census file upload is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const updateField = <K extends keyof EmployeeMedicalFormData>(field: K, value: EmployeeMedicalFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <FormField label="Total number of employees to be covered" required>
        <Input
          type="number"
          placeholder="Enter number of employees"
          value={formData.numberOfEmployees}
          onChange={(e) => updateField('numberOfEmployees', e.target.value)}
        />
        {errors.numberOfEmployees && <p className="text-red-500 text-sm">{errors.numberOfEmployees}</p>}
      </FormField>

      <FormField label="Budget per employee (AED)" required>
        <Input
          type="number"
          placeholder="Enter budget per employee in AED"
          value={formData.budgetPerEmployee}
          onChange={(e) => updateField('budgetPerEmployee', e.target.value)}
        />
        {errors.budgetPerEmployee && <p className="text-red-500 text-sm">{errors.budgetPerEmployee}</p>}
      </FormField>

      <FormField label="Comments (network preferences, etc.)" required>
        <Textarea
          placeholder="Enter any specific requirements, network preferences, or comments"
          value={formData.comments}
          onChange={(e) => updateField('comments', e.target.value)}
        />
        {errors.comments && <p className="text-red-500 text-sm">{errors.comments}</p>}
      </FormField>

      <FileUpload
        label="Upload Census"
        accept=".xls,.xlsx"
        required
        value={formData.censuFile}
        onChange={(file) => updateField('censuFile', file)}
      />
      {errors.censuFile && <p className="text-red-500 text-sm">{errors.censuFile}</p>}

      <Button type="submit" className="w-full">
        {buttonText}
      </Button>
    </form>
  );
};

export default EmployeeMedicalForm;
